/**
 * Seção Cenário Alvarás do Relatório
 * Year rows com dados históricos fixos (2022-2024), dados dinâmicos (2025) e forecast
 */
import { cn } from '@/lib/utils';
import { FileCheck } from 'lucide-react';
import { useCenarioAlvaras, useCenarioAlvarasHistorico, useSnapshots, type CenarioResponse } from '../hooks';
import { YearRow, RiscosBox } from './YearRow';

// Flag de rollback: oculta a lista de snapshots mensais sem remover o servico,
// o cron ou os endpoints. Reative alterando para true.
const SHOW_SNAPSHOTS_MENSAIS = false;

// ============================================================================
// DADOS HISTÓRICOS FIXOS - Extraídos do backup
// ============================================================================

const HISTORICAL_DATA_ALVARAS: Record<number, CenarioResponse> = {
  2022: {
    regular: 2768,
    regularPercent: 83,
    andamento: 113,
    andamentoPercent: 4,
    pendente: 445,
    pendentePercent: 13,
    total: 3326,
    regularAndamento: 2881,
    regularAndamentoPercent: 87,
  },
  2023: {
    regular: 2775,
    regularPercent: 91,
    andamento: 100,
    andamentoPercent: 3,
    pendente: 178,
    pendentePercent: 6,
    total: 3053,
    regularAndamento: 2875,
    regularAndamentoPercent: 94,
  },
  2024: {
    regular: 2868,
    regularPercent: 94,
    andamento: 3,
    andamentoPercent: 1,
    pendente: 165,
    pendentePercent: 5,
    total: 3036,
    regularAndamento: 2871,
    regularAndamentoPercent: 95,
  },
  2025: {
    regular: 2849,
    regularPercent: 97,
    andamento: 83,
    andamentoPercent: 3,
    pendente: 14,
    pendentePercent: 0,
    total: 2946,
    regularAndamento: 2932,
    regularAndamentoPercent: 100,
  },
};

// Forecast de Alvarás 2026
const FORECAST_2026_ALVARAS: CenarioResponse = {
  regular: 2795,
  regularPercent: 97.2,
  andamento: 76,
  andamentoPercent: 2.6,
  pendente: 4,
  pendentePercent: 0.2,
  total: 2875,
  regularAndamento: 2871,
  regularAndamentoPercent: 99.8,
};

interface CenarioAlvarasSectionProps {
  className?: string;
}

export function CenarioAlvarasSection({ className }: CenarioAlvarasSectionProps) {
  // Dados atuais (dinâmico)
  const { data: currentData, isLoading: loadingCurrent } = useCenarioAlvaras();
  
  // Lista de snapshots disponíveis
  const { data: snapshots } = useSnapshots();

  // Agrupa snapshots por ano
  const snapshotsByYear = new Map<number, { month: number; year: number }[]>();
  if (snapshots) {
    for (const snap of snapshots) {
      const arr = snapshotsByYear.get(snap.year) || [];
      arr.push({ month: snap.month, year: snap.year });
      snapshotsByYear.set(snap.year, arr);
    }
  }

  return (
    <section id="cenario-alvaras" className={cn('scroll-mt-20', className)}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <FileCheck className="w-8 h-8 text-blue-500" />
        <div>
          <h2 className="text-2xl font-bold">Cenário Alvarás</h2>
          <p className="text-sm text-muted-foreground">
            Evolução anual da conformidade de Alvará de Funcionamento
          </p>
        </div>
      </div>

      {/* Layout principal */}
      <div className="flex gap-6">
        {/* Riscos (sidebar) */}
        <div className="hidden lg:block w-64 flex-shrink-0">
          <RiscosBox type="alvara" className="sticky top-24" />
        </div>

        {/* Year Rows */}
        <div className="flex-1 space-y-4">
          {/* 2022 - Dados fixos */}
          <YearRow
            year={2022}
            data={HISTORICAL_DATA_ALVARAS[2022]}
            isLoading={false}
          />

          {/* 2023 - Dados fixos */}
          <YearRow
            year={2023}
            data={HISTORICAL_DATA_ALVARAS[2023]}
            isLoading={false}
          />

          {/* 2024 - Dados fixos */}
          <YearRow
            year={2024}
            data={HISTORICAL_DATA_ALVARAS[2024]}
            isLoading={false}
          />

          {/* 2025 - Dados fixos (fim do ano) */}
          <YearRow
            year={2025}
            data={HISTORICAL_DATA_ALVARAS[2025]}
            isLoading={false}
          />

          {/* 2026 - Dados dinâmicos (API) */}
          <YearRow
            year={2026}
            data={currentData || emptyData}
            isLoading={loadingCurrent}
          />

          {/* 2026 Forecast - Dados fixos (aguardando valores) */}
          {FORECAST_2026_ALVARAS.total > 0 && (
            <YearRow
              year={2026}
              isForecast
              data={FORECAST_2026_ALVARAS}
              isLoading={false}
            />
          )}

          {/* Snapshots mensais de 2026 (quando disponíveis) */}
          {SHOW_SNAPSHOTS_MENSAIS && snapshotsByYear.has(2026) && (
            <div className="mt-6">
              <div className="mb-2">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Snapshots Mensais 2026
                </span>
              </div>
              {snapshotsByYear.get(2026)!
                .sort((a, b) => b.month - a.month)
                .map(({ month, year: y }) => (
                  <SnapshotYearRow 
                    key={`${month}-${y}`}
                    month={month}
                    year={y}
                  />
                ))
              }
            </div>
          )}
        </div>
      </div>

      {/* Riscos em mobile */}
      <div className="lg:hidden mt-6">
        <RiscosBox type="alvara" />
      </div>
    </section>
  );
}

// ============================================================================
// SNAPSHOT YEAR ROW
// ============================================================================

interface SnapshotYearRowProps {
  month: number;
  year: number;
}

function SnapshotYearRow({ month, year }: SnapshotYearRowProps) {
  const { data, isLoading } = useCenarioAlvarasHistorico(month, year);

  return (
    <YearRow
      year={year}
      month={month}
      data={data || emptyData}
      isLoading={isLoading}
      className="opacity-90"
    />
  );
}

// Dados vazios para loading
const emptyData: CenarioResponse = {
  regular: 0,
  regularPercent: 0,
  andamento: 0,
  andamentoPercent: 0,
  pendente: 0,
  pendentePercent: 0,
  total: 0,
  regularAndamento: 0,
  regularAndamentoPercent: 0,
};
